import { type RouteObject, type LoaderFunction, type ActionFunction } from 'react-router-dom';
import { RouteErrorBoundary } from '@/shared/components/ErrorBoundary';

/**
 * Type for dynamic import of route module
 */
type RouteModule = {
  Component: React.ComponentType<unknown>;
  clientLoader?: LoaderFunction;
  clientAction?: ActionFunction;
  loader?: LoaderFunction;
  action?: ActionFunction;
  ErrorBoundary?: React.ComponentType<unknown>;
};

type ImportFn = () => Promise<RouteModule>;

/**
 * Helper to create a lazy route with default ErrorBoundary
 *
 * Usage:
 * lazyRoute('feed/:id', () => import('@/routes/feed.$id'), { ...options })
 */
export function lazyRoute(
  path: string | undefined,
  importFn: ImportFn,
  children?: RouteObject[]
): RouteObject {
  return {
    path,
    lazy: async () => {
      const module = await importFn();
      return {
        Component: module.Component,
        loader: module.clientLoader || module.loader,
        action: module.clientAction || module.action,
        ErrorBoundary: module.ErrorBoundary || RouteErrorBoundary,
      };
    },
    children,
  };
}

/**
 * Helper for index routes
 */
export function lazyIndexRoute(importFn: ImportFn): RouteObject {
  return {
    index: true,
    lazy: async () => {
      const module = await importFn();
      return {
        Component: module.Component,
        loader: module.clientLoader || module.loader,
        action: module.clientAction || module.action,
        ErrorBoundary: module.ErrorBoundary || RouteErrorBoundary,
      };
    },
  };
}
